import React, { useMemo } from 'react';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import {
  getNoCodePlatformOptions,
  getNoCodePlatformVisual,
  NoCodePlatformOption,
} from './noCodePlatformVisuals';

type Props = {
  open: boolean;
  anchorEl: HTMLElement | null;
  selectedId: string;
  onClose: () => void;
  onSelect: (option: NoCodePlatformOption) => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
};

/**
 * Platform picker for Widget Builder "Get embed code".
 * Options come from getStartedNoCodeOptions.json (dynamic).
 */
export default function EmbedCodePlatformMenu({
  open,
  anchorEl,
  selectedId,
  onClose,
  onSelect,
  onMouseEnter,
  onMouseLeave,
}: Props): React.JSX.Element | null {
  const options = useMemo(() => getNoCodePlatformOptions(), []);

  if (!open) return null;

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement="bottom-end"
      modifiers={[
        { name: 'offset', options: { offset: [0, 10] } },
        { name: 'preventOverflow', options: { padding: 8 } },
      ]}
      style={{ zIndex: 1500 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          elevation={0}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          sx={{
            width: 240,
            maxWidth: 'calc(100vw - 24px)',
            borderRadius: '18px',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 18px 44px rgba(27, 58, 92, 0.14)',
            border: '1px solid rgba(183, 208, 234, 0.55)',
            px: 1.5,
            pt: 1.75,
            pb: 1,
            maxHeight: 420,
            overflowY: 'auto',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          <Typography
            sx={{
              mb: 1.5,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: '#7B93B0',
              lineHeight: 1.2,
            }}
          >
            Choose your platform
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            {options.map((option) => {
              const selected = option.id === selectedId;
              const visual = getNoCodePlatformVisual(option.id);
              return (
                <Box
                  key={option.id}
                  role="option"
                  aria-selected={selected}
                  onClick={() => onSelect(option)}
                  sx={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    py: 1,
                    px: 0.75,
                    mx: -0.75,
                    borderRadius: '12px',
                    backgroundColor: selected ? 'rgba(47, 123, 201, 0.06)' : 'transparent',
                    color: selected ? '#2F7BC9' : '#0F172A',
                    transition: 'background-color 140ms ease, color 140ms ease',
                    '&:hover': {
                      backgroundColor: selected
                        ? 'rgba(47, 123, 201, 0.08)'
                        : 'rgba(247, 251, 255, 0.95)',
                      color: '#2F7BC9',
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      minWidth: 34,
                      borderRadius: '9px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: visual.tint,
                      color: visual.ink,
                      '& .MuiSvgIcon-root': { fontSize: 18 },
                    }}
                  >
                    {visual.icon}
                  </Box>

                  <Typography
                    sx={{
                      flex: 1,
                      fontWeight: 700,
                      fontSize: 14,
                      color: 'inherit',
                      lineHeight: 1.25,
                      textAlign: 'left',
                    }}
                  >
                    {option.name}
                  </Typography>

                  <ChevronRightIcon
                    sx={{
                      fontSize: 20,
                      color: 'inherit',
                      flexShrink: 0,
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
